import React from 'react';
import {
  Typography,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { CreateExerciseInput, Equipment } from '../services/types';
import { getImageUrl } from '../services/api';
import RichTextEditor from './common/RichTextEditor';

export interface ExerciseFormDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  formData: CreateExerciseInput;
  onFormChange: React.Dispatch<React.SetStateAction<CreateExerciseInput>>;
  onSave: () => void;
  onClose: () => void;
  // Filter options for dropdowns
  muscleGroups: string[];
  categories: string[];
  equipment: string[];
  equipmentEntities: Equipment[];
  // Image handling
  newAnatomyImage: string;
  onNewAnatomyImageChange: (url: string) => void;
  newInstructionImage: string;
  onNewInstructionImageChange: (url: string) => void;
  uploadingAnatomy: boolean;
  uploadingInstruction: boolean;
  uploadingMistakeImage: number | null;
  onAddAnatomyImage: () => void;
  onRemoveAnatomyImage: (index: number) => void;
  onAnatomyImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddInstructionImage: () => void;
  onRemoveInstructionImage: (index: number) => void;
  onInstructionImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddMistake: () => void;
  onRemoveMistake: (index: number) => void;
  onMistakeDescriptionChange: (index: number, description: string) => void;
  onMistakeImageUpload: (mistakeIndex: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMistakeImage: (mistakeIndex: number, imageIndex: number) => void;
}

const difficulties = ['beginner', 'intermediate', 'advanced'];

export const ExerciseFormDialog: React.FC<ExerciseFormDialogProps> = ({
  open,
  mode,
  formData,
  onFormChange,
  onSave,
  onClose,
  muscleGroups,
  categories,
  equipment,
  equipmentEntities,
  newAnatomyImage,
  onNewAnatomyImageChange,
  newInstructionImage,
  onNewInstructionImageChange,
  uploadingAnatomy,
  uploadingInstruction,
  uploadingMistakeImage,
  onAddAnatomyImage,
  onRemoveAnatomyImage,
  onAnatomyImageUpload,
  onAddInstructionImage,
  onRemoveInstructionImage,
  onInstructionImageUpload,
  onAddMistake,
  onRemoveMistake,
  onMistakeDescriptionChange,
  onMistakeImageUpload,
  onRemoveMistakeImage,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dialogId = mode;
  const title = mode === 'add' ? 'Add New Exercise' : 'Edit Exercise';
  const saveLabel = mode === 'add' ? 'Add Exercise' : 'Update Exercise';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Exercise Name"
              value={formData.name}
              onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid size={12}>
            <RichTextEditor
              label="Description"
              value={formData.description || ''}
              onChange={(html) => onFormChange({ ...formData, description: html })}
              placeholder="Кратко описание на упражнението..."
              minHeight={90}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Muscle Group (Legacy)</InputLabel>
              <Select
                value={formData.muscleGroup}
                onChange={(e) => onFormChange({ ...formData, muscleGroup: e.target.value })}
                input={<OutlinedInput label="Muscle Group (Legacy)" />}
                required
              >
                {muscleGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => onFormChange({ ...formData, category: e.target.value })}
                input={<OutlinedInput label="Category" />}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Muscle Groups (for body visualization - primary gets 1.0 load, secondary 0.5, tertiary 0.25)
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Primary Muscle</InputLabel>
              <Select
                value={formData.primaryMuscleGroup || ''}
                onChange={(e) => onFormChange({ ...formData, primaryMuscleGroup: e.target.value })}
                input={<OutlinedInput label="Primary Muscle" />}
              >
                <MenuItem value="">None</MenuItem>
                {muscleGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Secondary Muscle</InputLabel>
              <Select
                value={formData.secondaryMuscleGroup || ''}
                onChange={(e) => onFormChange({ ...formData, secondaryMuscleGroup: e.target.value })}
                input={<OutlinedInput label="Secondary Muscle" />}
              >
                <MenuItem value="">None</MenuItem>
                {muscleGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Tertiary Muscle</InputLabel>
              <Select
                value={formData.tertiaryMuscleGroup || ''}
                onChange={(e) => onFormChange({ ...formData, tertiaryMuscleGroup: e.target.value })}
                input={<OutlinedInput label="Tertiary Muscle" />}
              >
                <MenuItem value="">None</MenuItem>
                {muscleGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Equipment (Legacy)</InputLabel>
              <Select
                value={formData.equipment}
                onChange={(e) => onFormChange({ ...formData, equipment: e.target.value })}
                input={<OutlinedInput label="Equipment (Legacy)" />}
                required
              >
                {equipment.map((eq) => (
                  <MenuItem key={eq} value={eq}>
                    {eq}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Оборудване</InputLabel>
              <Select
                value={formData.equipmentEntity || ''}
                onChange={(e) => onFormChange({ ...formData, equipmentEntity: e.target.value || null })}
                input={<OutlinedInput label="Оборудване" />}
              >
                <MenuItem value="">
                  <em>Без оборудване</em>
                </MenuItem>
                {equipmentEntities.map((eq) => (
                  <MenuItem key={eq.id} value={`/api/equipment/${eq.id}`}>
                    {eq.name} ({eq.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={formData.difficulty}
                onChange={(e) => onFormChange({ ...formData, difficulty: e.target.value })}
                input={<OutlinedInput label="Difficulty" />}
                required
              >
                {difficulties.map((diff) => (
                  <MenuItem key={diff} value={diff}>
                    {diff}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <RichTextEditor
              label="Instructions"
              value={formData.instructions || ''}
              onChange={(html) => onFormChange({ ...formData, instructions: html })}
              placeholder="Стъпки за изпълнение..."
              minHeight={140}
            />
          </Grid>

          {/* YouTube Video URL */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              placeholder="https://www.youtube.com/watch?v=..."
              value={formData.youtubeVideoUrl}
              onChange={(e) => onFormChange({ ...formData, youtubeVideoUrl: e.target.value })}
            />
          </Grid>

          {/* Caution Notes */}
          <Grid size={12}>
            <RichTextEditor
              label="ВНИМАВАЙ (Предупреждения)"
              value={formData.cautionNotes || ''}
              onChange={(html) => onFormChange({ ...formData, cautionNotes: html })}
              placeholder="Важни насоки за безопасност..."
              minHeight={90}
            />
          </Grid>

          {/* Anatomy Images */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Анатомични Снимки</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`anatomy-image-upload-${dialogId}`}
                type="file"
                onChange={onAnatomyImageUpload}
              />
              <label htmlFor={`anatomy-image-upload-${dialogId}`}>
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  disabled={uploadingAnatomy}
                >
                  {uploadingAnatomy ? 'Качване...' : 'Качи Снимка'}
                </Button>
              </label>
              <TextField
                fullWidth={isMobile}
                sx={!isMobile ? { flex: 1 } : undefined}
                size="small"
                placeholder="Или постави URL на снимка"
                value={newAnatomyImage}
                onChange={(e) => onNewAnatomyImageChange(e.target.value)}
              />
              <Button
                onClick={onAddAnatomyImage}
                variant="outlined"
                size="small"
                disabled={!newAnatomyImage.trim()}
              >
                Добави URL
              </Button>
            </Box>
            {formData.anatomyImages && formData.anatomyImages.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.anatomyImages.map((url, index) => (
                  <Box key={index} position="relative">
                    <img
                      src={getImageUrl(url)}
                      alt={`Anatomy ${index + 1}`}
                      style={{
                        width: isMobile ? '80px' : '100px',
                        height: isMobile ? '80px' : '100px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '2px solid #1976d2',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => onRemoveAnatomyImage(index)}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'red',
                        color: 'white',
                        padding: '2px',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Instruction Images */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Инструкционни Снимки</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`instruction-image-upload-${dialogId}`}
                type="file"
                onChange={onInstructionImageUpload}
              />
              <label htmlFor={`instruction-image-upload-${dialogId}`}>
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  disabled={uploadingInstruction}
                >
                  {uploadingInstruction ? 'Качване...' : 'Качи Снимка'}
                </Button>
              </label>
              <TextField
                fullWidth={isMobile}
                sx={!isMobile ? { flex: 1 } : undefined}
                size="small"
                placeholder="Или постави URL на снимка"
                value={newInstructionImage}
                onChange={(e) => onNewInstructionImageChange(e.target.value)}
              />
              <Button
                onClick={onAddInstructionImage}
                variant="outlined"
                size="small"
                disabled={!newInstructionImage.trim()}
              >
                Добави URL
              </Button>
            </Box>
            {formData.instructionImages && formData.instructionImages.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.instructionImages.map((url, index) => (
                  <Box key={index} position="relative">
                    <img
                      src={getImageUrl(url)}
                      alt={`Instruction ${index + 1}`}
                      style={{
                        width: isMobile ? '80px' : '100px',
                        height: isMobile ? '80px' : '100px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '2px solid #dc004e',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => onRemoveInstructionImage(index)}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'red',
                        color: 'white',
                        padding: '2px',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Common Mistakes */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Чести грешки</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={onAddMistake}
                sx={{ borderColor: '#ff9800', color: '#ff9800' }}
              >
                Добави грешка
              </Button>
            </Box>
            {(formData.commonMistakes || []).map((mistake, mIndex) => (
              <Card key={mIndex} variant="outlined" sx={{ p: 1.5, mb: 1, borderColor: '#ff9800' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Грешка #{mIndex + 1}
                  </Typography>
                  <IconButton size="small" onClick={() => onRemoveMistake(mIndex)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Описание на грешката"
                  multiline
                  rows={2}
                  value={mistake.description}
                  onChange={(e) => onMistakeDescriptionChange(mIndex, e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`mistake-image-upload-${dialogId}-${mIndex}`}
                    type="file"
                    onChange={(e) => onMistakeImageUpload(mIndex, e)}
                  />
                  <label htmlFor={`mistake-image-upload-${dialogId}-${mIndex}`}>
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      disabled={uploadingMistakeImage === mIndex}
                      sx={{ borderColor: '#ff9800', color: '#ff9800' }}
                    >
                      {uploadingMistakeImage === mIndex ? 'Качване...' : 'Качи снимка'}
                    </Button>
                  </label>
                </Box>
                {mistake.images.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {mistake.images.map((url, imgIndex) => (
                      <Box key={imgIndex} position="relative">
                        <img
                          src={getImageUrl(url)}
                          alt={`Mistake ${mIndex + 1} img ${imgIndex + 1}`}
                          style={{
                            width: isMobile ? '80px' : '100px',
                            height: isMobile ? '80px' : '100px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '2px solid #ff9800',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => onRemoveMistakeImage(mIndex, imgIndex)}
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: 'red',
                            color: 'white',
                            padding: '2px',
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">{saveLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExerciseFormDialog;
